import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandler, Inject, Injectable, Injector, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { FirebaseError } from 'firebase/app';
import { ToastrService } from 'ngx-toastr';
import { NavigationRoutes } from '../navigation';

@Injectable()
export class ErrorsHandler implements ErrorHandler {

  // private readonly REFRESH_PAGE_ON_TOAST_CLICK_MESSAGE: string = "An error occurred: Please click this message to refresh";
  private readonly DEFAULT_ERROR_TITLE: string = 'Something went wrong';

  // ErrorHandler is created before the providers. So we need Injector for dependency injection
  constructor(@Inject(Injector) private injector: Injector, private zone: NgZone) {
  }

  handleError(error: Error | HttpErrorResponse | FirebaseError) {
    const router = this.injector.get(Router);

    if (this.handleFirebaseError(router, error as FirebaseError)) {
      this.showConsoleError(error, router.url);
      return;
    }

    if (this.handleHttpErrorResponse(router, error)) {
      this.showConsoleError(error, router.url);
      return;
    }

    this.navigateToGeneralError(router, error);

    // Log the error anyway
    this.showConsoleError(error, router.url);
  }

  private handleFirebaseError(router: Router, error: FirebaseError): boolean {
    if (error.name === 'FirebaseError' && error.code === 'permission-denied') {
      // this.zone.run(() => router.navigateByUrl('/' + NavigationRoutes.ForbiddenRoute)).then();
      return true;
    }
    return false;
  }

  private handleHttpErrorResponse(router: Router, error: Error): boolean {
    if (error instanceof HttpErrorResponse) {
      // Server error happened
      if (!navigator.onLine) {
        // No Internet connection
        this.showError('No Internet Connection');
        return true;
      }
      // Http Error
      const httpErrorCode = error.status;
      switch (httpErrorCode) {
        // case 400: //BAD_REQUEST
        //   this.showError(error.message);
        //   break;
        // case 401: //UNAUTHORIZED
        //   router.navigateByUrl("/login");
        //   break;
        case 403: // FORBIDDEN
          router.navigateByUrl('/' + NavigationRoutes.ForbiddenRoute);
          break;
        // case 404: //NOT FOUND
        // this.showError("not found");
        // break;
        // default:
        //   this.showError(this.REFRESH_PAGE_ON_TOAST_CLICK_MESSAGE);
      }
     //do not show http request with code 200(ok) to the user as an error, just in console 
     //this happens when the request is cancelled (by javascript/chrome/leave page/destroyed control)
     //and usually another one it's made
     if (httpErrorCode!=200) {
        this.showError(this.transformError(error));
     }
     return true;
    }
    return false;
  }

  private navigateToGeneralError(router, error) {
    // Client Error Happend
    // Navigation triggered outside Angular zone: workaround
    this.zone.run(() => router.navigateByUrl('/' +
      NavigationRoutes.GeneralErrorRoute, { queryParams: { error, replaceUrl: router.url }, skipLocationChange: true }));
  }

  private showConsoleError(error, url) {
    console.error('ODQ-Error...:', error, url);
  }

  private showError(message: string) {
    this.toastrService.error(
      message,
      this.DEFAULT_ERROR_TITLE,
      {
        closeButton: true,
        timeOut: 10000,
        onActivateTick: true,
      },
    );
  }

  // Need to get ToastrService from injector rather than constructor injection to avoid cyclic dependency error
  private get toastrService(): ToastrService {
    return this.injector.get(ToastrService);
  }

  private transformError(error: HttpErrorResponse) {
    let errorMessage = 'An unknown error has occurred';
    if (error.error instanceof ErrorEvent) {
      errorMessage = `Error! ${error.error.message}`;
    } else if (error.status) {
      errorMessage = `Request failed with status ${error.status}: ${error.statusText} [${error.url}]`;
    }
    return errorMessage;
  }
}
